import type {
  IsoInstant,
  LogbookApproaches,
  LogbookEntry,
  LogbookEntryEdit,
  LogbookEntryMergeSource,
  LogbookLandings,
  LogbookTakeoffs,
  LogbookTimeMatrix,
} from '@/lib/db';

import { columnById, entryTotalHours } from './columns';
import { roundDecimalHours } from './time';

/**
 * Folding two recordings of one flight into one logbook entry.
 *
 * The app drafts an entry per track, and a track is a recording, not a
 * flight: FR24 drops coverage over the ranges and hands back two files for
 * one trip; the same leg arrives as an FR24 export *and* an EFB GPX with
 * different fixes, so `contentHash` and the overlap check both let it
 * through; a touch-and-go at a second field can end one recording and start
 * another. The pilot flew once and writes one line in the bound book, so the
 * app has to be able to make one line too — with the hours, takeoffs and
 * landings **added together**, which is the whole point and the one thing a
 * pilot cannot get by deleting the spare row.
 *
 * Four rules hold:
 *
 *  1. **Counts add, identity does not.** Every hours column, every takeoff and
 *     every landing is a sum across the entries being merged. Everything else
 *     — registration, type, PIC, class — is chosen, and any disagreement is
 *     reported rather than silently resolved.
 *  2. **The earliest entry survives.** It keeps its id, so the links into it
 *     (a flight page, an anchor in the grid) still land; it keeps its
 *     `flightId`, so the merged entry still has a flight and a track behind
 *     it. The others are deleted — but their flights and tracks are not, and
 *     `mergedFrom` remembers which ones they were.
 *  3. **Only drafts merge.** An approved entry is locked everywhere else
 *     (`applyCellEdit`), and adding hours into one behind the pilot's back
 *     would move a number that has already been counted. Put it back in
 *     review first; that door already exists.
 *  4. **Nothing is auto-committed.** The result is a draft with a new set of
 *     numbers in it, waiting on the same review any other draft gets —
 *     `NZ-LOGBOOK.md` §2.3.
 *
 * This module is pure: it takes entries and returns entries. The grid does
 * the writing, which is what lets a merge sit on the same undo stack as every
 * other edit.
 */

type TimeKey = Exclude<keyof LogbookTimeMatrix, 'engineClass'>;

const SUMMED_TIME_KEYS: readonly TimeKey[] = [
  'dayHr',
  'nightHr',
  'picHr',
  'commandPracticeHr',
  'dualHr',
  'coPilotHr',
  'instructorHr',
  'actualInstrumentHr',
  'simulatedInstrumentHr',
  'groundInstrumentHr',
  'ifrCrossCountryHr',
];

const SUMMED_TAKEOFF_KEYS: readonly (keyof LogbookTakeoffs)[] = ['day', 'night'];
const SUMMED_APPROACH_KEYS: readonly (keyof LogbookApproaches)[] = ['nonPrecision', 'precision'];
const SUMMED_LANDING_KEYS: readonly (keyof LogbookLandings)[] = ['day', 'night', 'touchAndGo'];

/** Column ids whose values a merge adds together, rather than picking between. */
export const MERGE_SUMMED_FIELDS: readonly string[] = [
  ...SUMMED_TIME_KEYS.map((key) => `time.${key}`),
  ...SUMMED_APPROACH_KEYS.map((key) => `approaches.${key}`),
  ...SUMMED_TAKEOFF_KEYS.map((key) => `takeoffs.${key}`),
  ...SUMMED_LANDING_KEYS.map((key) => `landings.${key}`),
];

/** A field the entries disagreed on, with the value kept and the ones dropped. */
export interface MergeConflict {
  /** Column id, so a caller can name the column the way the grid's header does. */
  field: string;
  label: string;
  kept: string;
  discarded: string[];
}

export type MergeOutcome =
  | { ok: false; reason: string }
  | {
      ok: true;
      /** The surviving entry, with every count summed. Write this. */
      entry: LogbookEntry;
      /** The entries it absorbed, as they were. Delete these — and keep them for undo. */
      absorbed: LogbookEntry[];
      conflicts: MergeConflict[];
    };

/**
 * Why these entries cannot be merged, or `undefined` if they can.
 *
 * Separate from `mergeEntries` so a button can be disabled with the reason in
 * its tooltip before anyone clicks it.
 *
 * Only two things are refused, and neither is "these don't look like the same
 * flight": a pilot merging two entries is asserting they are, and the app
 * knowing better than the pilot about their own flying is not a position it
 * is in. A different aircraft or a different day is reported as a conflict
 * instead — visible, and undoable.
 */
export function mergeBlockedReason(entries: readonly LogbookEntry[]): string | undefined {
  const unique = uniqueById(entries);
  if (unique.length < 2) {
    return 'Select two or more entries to merge.';
  }
  if (unique.some((entry) => !entry.isDraft)) {
    return 'Approved entries are locked. Move them back to draft before merging.';
  }
  return undefined;
}

/**
 * Adds the selected entries into one.
 *
 * @param entries  two or more entries, in any order. The earliest survives.
 * @param now      merge timestamp, injectable so tests are not clock-dependent.
 */
export function mergeEntries(
  entries: readonly LogbookEntry[],
  now: IsoInstant = new Date().toISOString(),
): MergeOutcome {
  const blocked = mergeBlockedReason(entries);
  if (blocked !== undefined) {
    return { ok: false, reason: blocked };
  }

  const sorted = uniqueById(entries).sort(byFlownOrder);
  const [survivor, ...absorbed] = sorted;
  if (survivor === undefined) {
    // Unreachable: `mergeBlockedReason` already required two entries. Narrowing
    // it here is cheaper than a non-null assertion the lint config would refuse.
    return { ok: false, reason: 'Select two or more entries to merge.' };
  }

  const time: LogbookTimeMatrix = {
    ...survivor.time,
    ...Object.fromEntries(
      SUMMED_TIME_KEYS.map((key) => [
        key,
        roundDecimalHours(sumBy(sorted, (entry) => entry.time[key])),
      ]),
    ),
  };
  const takeoffs: LogbookTakeoffs = {
    day: sumBy(sorted, (entry) => entry.takeoffs.day),
    night: sumBy(sorted, (entry) => entry.takeoffs.night),
  };
  const approaches: LogbookApproaches = {
    nonPrecision: sumBy(sorted, (entry) => entry.approaches.nonPrecision),
    precision: sumBy(sorted, (entry) => entry.approaches.precision),
  };
  const landings: LogbookLandings = {
    day: sumBy(sorted, (entry) => entry.landings.day),
    night: sumBy(sorted, (entry) => entry.landings.night),
    touchAndGo: sumBy(sorted, (entry) => entry.landings.touchAndGo),
  };

  const from = firstDefined(sorted, (entry) => entry.from);
  // The route the pilot flew, not the first recording's: the merged leg ends
  // where the *last* recording ended.
  const to = firstDefined([...sorted].reverse(), (entry) => entry.to);

  const merged: LogbookEntry = {
    ...survivor,
    // Sorted earliest first, so this is the earliest date — a flight that ran
    // past midnight is logged on the day it started, the same rule
    // `LocalDate` states for every other date in the app.
    date: survivor.date,
    ...present({
      aircraftId: firstDefined(sorted, (entry) => entry.aircraftId),
      registration: firstDefined(sorted, (entry) => entry.registration),
      aircraftType: firstDefined(sorted, (entry) => entry.aircraftType),
      otherCrew: joinUnique(sorted, (entry) => entry.otherCrew, ' · '),
      from,
      to,
      intermediate: mergedIntermediate(sorted, from, to),
      remarks: joinUnique(sorted, (entry) => entry.remarks, ' · '),
    }),
    pic: firstDefined(sorted, (entry) => (entry.pic.trim() === '' ? undefined : entry.pic)) ?? '',
    time,
    approaches,
    takeoffs,
    landings,
    isDraft: true,
    derivedFields: [],
    history: [],
    mergedFrom: [
      ...(survivor.mergedFrom ?? []),
      ...absorbed.flatMap((entry) => [
        ...(entry.mergedFrom ?? []),
        {
          entryId: entry.id,
          flightId: entry.flightId,
          date: entry.date,
          hours: entryTotalHours(entry),
          at: now,
        } satisfies LogbookEntryMergeSource,
      ]),
    ],
    updatedAt: now,
  };

  // Both of these read the merged entry, so they are computed against it
  // rather than folded into the object literal above.
  const withProvenance: LogbookEntry = {
    ...merged,
    derivedFields: mergedDerivedFields(sorted, merged),
    history: mergedHistory(sorted, survivor, merged, now),
  };

  return {
    ok: true,
    entry: withProvenance,
    absorbed,
    conflicts: findConflicts(sorted, withProvenance),
  };
}

/**
 * The merged entry, described in one line: what a caller tells the pilot
 * happened, and what a test asserts on.
 */
export function describeMerge(outcome: Extract<MergeOutcome, { ok: true }>): string {
  const count = outcome.absorbed.length + 1;
  const hours = entryTotalHours(outcome.entry).toFixed(2);
  const landings = outcome.entry.landings.day + outcome.entry.landings.night;
  const base = `Merged ${String(count)} entries into one: ${hours} hr, ${String(landings)} ${
    landings === 1 ? 'landing' : 'landings'
  }.`;
  if (outcome.conflicts.length === 0) {
    return base;
  }
  const fields = outcome.conflicts.map((conflict) => conflict.label).join(', ');
  return `${base} They disagreed on ${fields} — kept the earliest entry's, check before approving.`;
}

/** Entries by id, first occurrence wins — the same row checked twice is one row. */
function uniqueById(entries: readonly LogbookEntry[]): LogbookEntry[] {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    if (seen.has(entry.id)) {
      return false;
    }
    seen.add(entry.id);
    return true;
  });
}

/**
 * The order the legs were flown in, as far as the entries themselves can say.
 *
 * `date` first, then `createdAt` — a track imported from a file carries the
 * time it was imported, not the time it was flown, but two recordings of one
 * flight are imported in the order the files were read, which is the best
 * evidence available without loading both tracks. `id` breaks the tie so the
 * result never depends on the order rows happened to be checked in.
 */
function byFlownOrder(a: LogbookEntry, b: LogbookEntry): number {
  if (a.date !== b.date) {
    return a.date.localeCompare(b.date);
  }
  if (a.createdAt !== b.createdAt) {
    return a.createdAt.localeCompare(b.createdAt);
  }
  return a.id.localeCompare(b.id);
}

function sumBy(entries: readonly LogbookEntry[], read: (entry: LogbookEntry) => number): number {
  return entries.reduce((total, entry) => total + read(entry), 0);
}

function firstDefined(
  entries: readonly LogbookEntry[],
  read: (entry: LogbookEntry) => string | undefined,
): string | undefined {
  for (const entry of entries) {
    const value = read(entry);
    if (value !== undefined && value.trim() !== '') {
      return value;
    }
  }
  return undefined;
}

/**
 * The entries of an object that actually have a value.
 *
 * `exactOptionalPropertyTypes` is on, so an optional field a merge found
 * nothing for has to be an *absent key* rather than one set to `undefined` —
 * and eight `...(x === undefined ? {} : { x })` spreads in one object literal
 * is not something anyone can read.
 */
function present<T extends Record<string, unknown>>(
  values: T,
): { [K in keyof T]?: Exclude<T[K], undefined> } {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined)) as {
    [K in keyof T]?: Exclude<T[K], undefined>;
  };
}

function joinUnique(
  entries: readonly LogbookEntry[],
  read: (entry: LogbookEntry) => string | undefined,
  separator: string,
): string | undefined {
  const parts: string[] = [];
  for (const entry of entries) {
    const value = read(entry)?.trim();
    if (value !== undefined && value !== '' && !parts.includes(value)) {
      parts.push(value);
    }
  }
  return parts.length === 0 ? undefined : parts.join(separator);
}

/**
 * Where the merged flight went in between.
 *
 * Two recordings of one flight meet somewhere — the first one's arrival, the
 * second one's departure — and that place is a stop the merged entry flew
 * through, not the end of anything. It joins the Intermediate list, along
 * with whatever each entry already had there, minus the two ends of the
 * merged route so the column doesn't repeat From and To back at the reader.
 */
function mergedIntermediate(
  sorted: readonly LogbookEntry[],
  from: string | undefined,
  to: string | undefined,
): string[] | undefined {
  const parts: string[] = [];
  const push = (value: string | undefined) => {
    const trimmed = value?.trim();
    if (
      trimmed !== undefined &&
      trimmed !== '' &&
      trimmed !== from &&
      trimmed !== to &&
      !parts.includes(trimmed)
    ) {
      parts.push(trimmed);
    }
  };

  sorted.forEach((entry, index) => {
    if (index > 0) {
      push(sorted[index - 1]?.to);
      push(entry.from);
    }
    entry.intermediate?.forEach(push);
  });

  return parts.length === 0 ? undefined : parts;
}

/**
 * Which fields the merged entry can still honestly call "from the track".
 *
 * A summed column is only still the track's own number if *every* entry that
 * went into the sum said so — one hand-typed hour in the total makes the
 * whole of it the pilot's. Everything else is derived if some entry that
 * called it derived also carried the value that survived: the badge follows
 * the value, not the row.
 *
 * This matters beyond the badge. `review.ts` gates approval on
 * takeoff/landing counts still being an unreviewed guess, so a merge that
 * kept those marked derived keeps the gate closed, which is right: a summed
 * count nobody has looked at is exactly the number worth a second look.
 */
function mergedDerivedFields(sorted: readonly LogbookEntry[], merged: LogbookEntry): string[] {
  const fields: string[] = [];
  const candidates = new Set(sorted.flatMap((entry) => entry.derivedFields));

  for (const field of candidates) {
    if (MERGE_SUMMED_FIELDS.includes(field)) {
      if (sorted.every((entry) => entry.derivedFields.includes(field))) {
        fields.push(field);
      }
      continue;
    }
    const column = columnById(field);
    if (column === undefined) {
      continue;
    }
    const mergedValue = column.read(merged);
    if (
      sorted.some(
        (entry) => entry.derivedFields.includes(field) && column.read(entry) === mergedValue,
      )
    ) {
      fields.push(field);
    }
  }

  return fields;
}

/**
 * Every change all the entries had recorded, oldest first, plus one edit per
 * column the merge itself moved.
 *
 * `NZ-LOGBOOK.md` §2.4 keeps prior values rather than overwriting them, and a
 * merge overwrites more at once than any keystroke does. The absorbed
 * entries' own histories come along because the rows they belonged to are
 * gone: dropping them would be the one place the append-only habit quietly
 * didn't hold.
 */
function mergedHistory(
  sorted: readonly LogbookEntry[],
  survivor: LogbookEntry,
  merged: LogbookEntry,
  now: IsoInstant,
): LogbookEntryEdit[] {
  const inherited = sorted
    .flatMap((entry) => entry.history)
    .sort((a, b) => a.at.localeCompare(b.at));

  const changes: LogbookEntryEdit[] = [];
  for (const column of MERGE_HISTORY_COLUMNS) {
    const before = column.read(survivor);
    if (column.read(merged) !== before) {
      changes.push({ at: now, field: column.id, previousValue: before });
    }
  }

  return [...inherited, ...changes];
}

/** Every column, resolved once: `mergedHistory` walks all of them per merge. */
const MERGE_HISTORY_COLUMNS = MERGE_SUMMED_FIELDS.concat([
  'registration',
  'aircraftType',
  'pic',
  'from',
  'to',
  'intermediate',
  'remarks',
])
  .map((id) => columnById(id))
  .filter((column) => column !== undefined);

/**
 * Fields where the entries said different things and the merge had to choose.
 *
 * Only reported for columns a merge *picks* — a summed column cannot
 * disagree with itself. The pilot sees these because a merge across two
 * registrations is usually a mis-click, and an app that fixed it silently
 * would be hiding the one thing worth noticing.
 */
function findConflicts(sorted: readonly LogbookEntry[], merged: LogbookEntry): MergeConflict[] {
  const conflicts: MergeConflict[] = [];

  for (const id of ['date', 'registration', 'aircraftType', 'pic', 'time.engineClass']) {
    const column = columnById(id);
    if (column === undefined) {
      continue;
    }
    const kept = column.read(merged);
    const discarded: string[] = [];
    for (const entry of sorted) {
      const value = column.read(entry);
      if (value !== '' && value !== kept && !discarded.includes(value)) {
        discarded.push(value);
      }
    }
    if (discarded.length > 0) {
      conflicts.push({ field: id, label: column.label, kept, discarded });
    }
  }

  return conflicts;
}
